import {useRouter} from 'next/router';
import {useEffect, useMemo} from 'react';
import {DisplayAdContainer} from '../../../common/containers/DisplayAdContainer/DisplayAdContainer';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {isTrue} from '../../../common/shared/utils/isTrue';
import {twMerge} from '../../../common/twMerge';
import useTypedDispatch from '../../../common/useTypedDispatch';
import {LOCAL_STORAGE_TODAY_GIFT_LAST_ADDED_DATE_KEY} from '../../../config/constants';
import {TabPositionControl} from '../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {Text} from '../../../design-system/atoms/Text/Text';
import {MainTab} from '../../../design-system/organisms/MainTab/MainTab';
import {useDialog} from '../../../dialog/useDialog.hook';
import {
  AgeGrade,
  SortOption,
  TodayGiftListQuery,
} from '../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {useLogin} from '../../../user/useLogin.hook';
import {useAppConfigQuery} from '../../../wrapper';
import {useClickGiftButton} from '../hook/useClickGiftButton';
import {TodayGiftListToolbar} from '../presentational/TodayGiftListToolbar';
import {setIsUpdated} from '../state/todayGiftSlice';
import {getReceivedTicketInfoFromQuery} from '../util/getReceivedTicketInfoFromQuery';
import {TodayGiftHelixListContainer} from './TodayGiftHelixListContainer';
import {TodayGiftListContainer} from './TodayGiftListContainer';
import {TodayGiftPromotionListContainer} from './TodayGiftPromotionListContainer';

export function TodayGiftContainer({
  data,
  categoryUid,
  categoryList: _categoryList,
  subCategoryUid,
}: {
  data: TodayGiftListQuery;
  categoryUid: string;
  categoryList: SortOption[];
  subCategoryUid?: string;
}) {
  const result = useAppConfigQuery();
  const giftLastAddedDate = result.data?.appConfig?.appBadge?.giftLastAddedDate;
  const {isLogin} = useLogin();
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const {onClickGiftButton} = useClickGiftButton({
    categoryUid,
    subCategoryUid,
  });
  const {isMobile, isWebView} = requestContext.requestInfo.display;

  useEffect(() => {
    if (router.isReady) {
      // 비로그인 상태에서 선물받기 버튼을 눌렀을 경우, 쿼리에 해당 선물에 대한 정보가 포함된 채로 페이지에 진입합니다.
      // 이 경우, 페이지에 진입하자마자 해당 선물을 받은 후 정보를 보여줘야 합니다.
      if (router.query && isLogin) {
        const {title, ticketUid, ageGrade, isHelixGift, seriesId} =
          getReceivedTicketInfoFromQuery({
            query: router.query,
          });
        if (ticketUid !== undefined) {
          onClickGiftButton({
            title,
            ticketUid: Number(ticketUid),
            seriesId: Number(seriesId),
            ageGrade: ageGrade as AgeGrade,
            isHelixGift: isTrue(isHelixGift),
          });
          router.replace(
            `/today-gift/${categoryUid}${
              subCategoryUid ? `?subcategory_uid=${subCategoryUid}` : ''
            }`,
            undefined,
            {
              shallow: true,
              scroll: false,
            }
          );
        }
      }
    }
  }, [categoryUid, isLogin, router, onClickGiftButton, subCategoryUid]);

  useEffect(() => {
    if (isLogin && giftLastAddedDate) {
      // 선물함 페이지 진입 시 로컬 스토리지의 giftLastAddedDate 정보 갱신
      window.localStorage.setItem(
        LOCAL_STORAGE_TODAY_GIFT_LAST_ADDED_DATE_KEY,
        giftLastAddedDate
      );
      dispatch(setIsUpdated(false));
    }
  }, [dispatch, giftLastAddedDate, isLogin]);

  const categoryList = useMemo(() => {
    return _categoryList.map(({name, param}) => ({
      id: param,
      text: name,
      href: `/today-gift/${param}`,
    }));
  }, [_categoryList]);

  const currentCategoryName = categoryList.find(
    category => category.id === categoryUid
  )?.text;
  const currentSubCategoryName = data.todayGiftList?.selectedSubCategory?.name;

  const {Dialog, DialogWrapper, showDialog} = useDialog({
    isMobile,
  });

  const hasTodayGiftList =
    !!data?.todayGiftList?.list.length ||
    !!data?.todayGiftList?.helixList.length ||
    !!data?.todayGiftList?.promotionList.length;

  return (
    <>
      <div
        className={twMerge(
          'flex grow flex-col',
          isMobile && 'mb-15pxr',
          !isMobile && 'mb-84pxr'
        )}
      >
        <TabPositionControl
          depth={1}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <MainTab
            className={!isMobile ? 'px-15pxr' : 'p-0'}
            isMobile={isMobile}
            title={t('common_tab_giftbox')}
            tabItems={categoryList}
            currentTabItemId={categoryUid}
            showBorder={true}
            tabItemTiaraObject={{
              customProps: {
                page_category_filter: currentCategoryName,
                page_genre_filter: currentSubCategoryName,
              },
            }}
          />
        </TabPositionControl>
        <TabPositionControl
          depth={2}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <TodayGiftListToolbar
            ticketCount={data.todayGiftList?.receivableTicketCount}
            currentSubCategoryName={currentSubCategoryName}
            onClickSubCategory={showDialog}
            currentCategoryName={currentCategoryName}
          />
        </TabPositionControl>
        <DialogWrapper>
          <Dialog.Dimmed />
          <Dialog.List className="space-y-0">
            {data.todayGiftList?.subCategoryList &&
              data.todayGiftList.subCategoryList.map(subcategory => (
                <Dialog.Option
                  key={subcategory.id}
                  isChecked={
                    subcategory.id ===
                    data.todayGiftList?.selectedSubCategory?.id
                  }
                  {...putTiaraObjectProps({
                    click: {
                      layer1: '리스팅옵션',
                      copy: subcategory.name,
                    },
                    customProps: {
                      page_category_filter: currentCategoryName,
                      page_genre_filter: currentSubCategoryName,
                    },
                  })}
                  onClick={e => {
                    LOG_CLICK_EVENT('장르필터_클릭', e, () => {
                      router.push({
                        pathname: location.pathname,
                        query: {
                          subcategory_uid: subcategory.param,
                        },
                      });
                    });
                  }}
                >
                  {subcategory.name}
                </Dialog.Option>
              ))}
          </Dialog.List>
        </DialogWrapper>
        {isMobile && (
          <DisplayAdContainer
            displayAd={data.todayGiftList?.displayAd}
            className="mx-11pxr mb-8pxr"
          />
        )}
        {!hasTodayGiftList ? (
          <div className="min-h-340pxr flex-center-full">
            <Text className="font-medium1 pb-36pxr text-theme-solid-100">
              {t('giftbox_no_gift')}
            </Text>
          </div>
        ) : (
          <>
            {data.todayGiftList?.helixList &&
              data.todayGiftList.helixList.length > 0 && (
                <TodayGiftHelixListContainer
                  currentCategoryName={currentCategoryName}
                  currentSubCategoryName={currentSubCategoryName}
                  list={data.todayGiftList.helixList}
                  description={data.todayGiftList.helixDescription}
                  currentCategoryUid={categoryUid}
                  currentSubCategoryUid={subCategoryUid}
                />
              )}
            {data.todayGiftList?.promotionList &&
              data.todayGiftList?.promotionList.length > 0 && (
                <TodayGiftPromotionListContainer
                  currentCategoryName={currentCategoryName}
                  currentSubCategoryName={currentSubCategoryName}
                  list={data.todayGiftList.promotionList}
                  title={data.todayGiftList.promotionTitle}
                  currentCategoryUid={categoryUid}
                  currentSubCategoryUid={subCategoryUid}
                />
              )}
            {data.todayGiftList?.list &&
              data.todayGiftList?.list.length > 0 && (
                <TodayGiftListContainer
                  currentCategoryName={currentCategoryName}
                  currentSubCategoryName={currentSubCategoryName}
                  list={data.todayGiftList.list}
                  currentCategoryUid={categoryUid}
                  currentSubCategoryUid={subCategoryUid}
                />
              )}
          </>
        )}
      </div>
    </>
  );
}
